import { trackSignup } from "@/lib/tracking";
import { FundingDisclosure, useDisclosure } from "@/components/FundingDisclosure";
import { useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { DisclosureAgree } from "@/components/DisclosureGate";
import { LangToggle } from "@/pages/Legal";
import { useTour } from "@/components/tour/TourProvider";
import { LOGIN_TEXT } from "@/lib/legal";

const ERR_ES = {
  "Invalid email or password": "Correo electrónico o contraseña incorrectos",
  "An account with this email already exists": "Ya existe una cuenta con este correo electrónico",
  "Too many failed attempts. Try again in 15 minutes.": "Demasiados intentos fallidos. Inténtelo de nuevo en 15 minutos.",
  "This account has been deactivated": "Esta cuenta ha sido desactivada",
};

const safeNext = (n) => (n && /^\/dashboard(\/[\w-]*)*$/.test(n) ? n : "/dashboard");

function googleLogin(next) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + next;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const joined = useRef(false);
  const disc = useDisclosure();
  const { lang } = useTour();
  const T = LOGIN_TEXT[lang === "es" ? "es" : "en"];

  const next = safeNext(params.get("next"));
  if (user) return <Navigate to={joined.current ? `/welcome?next=${encodeURIComponent(next)}` : next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        if (!agreed) throw new Error(T.mustAgree);
        joined.current = true;
        try { await register(form.name, form.email, form.password, agreed); } catch (e3) { joined.current = false; throw e3; }
        trackSignup();
        return;
      }
      navigate(next);
    } catch (e2) {
      const m = formatErr(e2);
      setErr((lang === "es" && ERR_ES[m]) || m);
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-no-translate lang={lang === "es" ? "es" : "en"} data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 grid-bg" style={{ backgroundBlendMode: "overlay" }}>
        <Brand light />
        <div>
          <div className="font-mono text-xs tracking-[0.18em] uppercase text-emerald-400 mb-4">{T.tagline}</div>
          <h2 className="font-display text-5xl font-extrabold leading-[1.05]">{T.hero}</h2>
        </div>
        <p className="text-sm text-slate-400">© Peer Verify LLC</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md fade-up">
          <div className="flex justify-between items-center mb-10 lg:justify-end"><div className="lg:hidden"><Brand /></div><LangToggle /></div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">{mode === "login" ? T.welcome : T.create}</h1>
          <p className="text-slate-500 mt-2">{mode === "login" ? T.signInSub : T.registerSub}</p>
          <Button type="button" variant="outline" onClick={() => googleLogin(next)} data-testid="google-login-button" className="w-full h-11 mt-8 border-slate-300">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" /> {T.google}
          </Button>
          {mode === "register" && <p className="text-xs text-slate-400 mt-2 text-center" data-testid="google-disclosure-note">{disc.google}</p>}
          <div className="flex items-center gap-3 my-6 text-xs text-slate-400"><div className="h-px bg-slate-200 flex-1" />{T.orEmail}<div className="h-px bg-slate-200 flex-1" /></div>
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div><Label htmlFor="name">{T.name}</Label><Input id="name" required value={form.name} onChange={set("name")} className="h-11 mt-1.5" data-testid="auth-name-input" /></div>
            )}
            <div><Label htmlFor="email">{T.email}</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} className="h-11 mt-1.5" data-testid="auth-email-input" /></div>
            <div><Label htmlFor="password">{T.password}</Label><Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="h-11 mt-1.5" data-testid="auth-password-input" /></div>
            {mode === "register" && <DisclosureAgree checked={agreed} onChange={setAgreed} />}
            {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="auth-submit-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{mode === "login" ? T.signIn : T.createBtn}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            {mode === "login" ? T.newHere : T.have}
            <button className="text-emerald-700 font-semibold hover:underline" data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}>
              {mode === "login" ? T.createLink : T.signInLink}
            </button>
          </p>
          <FundingDisclosure className="mt-10" />
        </div>
      </div>
    </div>
  );
}
